"use client";

import { filterEntitiesArray, getImageProps, getVideoUrl } from "@utilities/payload";
import { LazyComponent } from "../BlockBentoGrid/LazyComponent";
import type { AudioSample } from "@mw/shared/payload-types";

/**
 * Renders demo components based on the component template (client-side only demos)
 * Uses the shared LazyComponent abstraction to avoid duplicating dynamic imports.
 */
export const ContentGridDemoComponent = ({ componentData }: { componentData: any }) => {
  const template = componentData.template;

  switch (template) {
    case "multimodalAgentsDemo":
      return <LazyComponent name='multimodalAgentsDemo' />;

    case "musicUIDemo":
      return <LazyComponent name='musicUIDemo' />;

    case "musicV2UIDemo":
      return <LazyComponent name='musicV2UIDemo' />;

    case "musicV2InpaintingDemo":
      return <LazyComponent name='musicV2InpaintingDemo' />;

    case "musicV2MarketplaceDemo":
      return <LazyComponent name='musicV2MarketplaceDemo' />;

    case "dubbingV2Demo":
      return <LazyComponent name='dubbingV2Demo' />;

    case "elevenMusicDemo":
      return <LazyComponent name='elevenMusicDemo' />;

    case "allInOneEditorDemo": {
      const data = componentData.allInOneEditorDemo;
      return (
        <LazyComponent
          name='allInOneEditorDemo'
          backgroundColor={data?.backgroundColor ?? "cream"}
          mainText={data?.mainText}
          secondaryText={data?.secondaryText}
          timelineImage={data?.timelineImage}
          frameImage={data?.frameImage}
        />
      );
    }

    case "chartsDemo": {
      const charts = componentData.chartsDemo?.charts;
      return (
        <LazyComponent
          name='chartsDemo'
          charts={charts?.map((chart: any) => ({
            label: chart.label,
            chart: {
              ...chart.chart,
              type: "bar",
            },
          }))}
        />
      );
    }

    case "languagesGlobeDemo":
      return <LazyComponent name='languagesGlobeDemo' />;

    case "scribeV1toV2Demo":
      return <LazyComponent name='scribeV1toV2Demo' />;

    case "callTranscriptionDemo":
      return <LazyComponent name='callTranscriptionDemo' />;

    case "wordPredictionDemo":
      return <LazyComponent name='wordPredictionDemo' />;

    case "beforeAfterDemo": {
      const data = componentData.beforeAfterDemo;
      return (
        <LazyComponent
          name='beforeAfterDemo'
          beforeImage={data?.beforeImage}
          afterImage={data?.afterImage}
          beforeLabel={data?.beforeLabel}
          afterLabel={data?.afterLabel}
        />
      );
    }

    case "lipSyncDemo":
      return <LazyComponent name='lipSyncDemo' />;

    case "lipSyncAvatarDemo":
      return <LazyComponent name='lipSyncAvatarDemo' />;

    case "ttsControlsDemo":
      return <LazyComponent name='ttsControlsDemo' />;

    case "voiceCloneDemo":
      return <LazyComponent name='voiceCloneDemo' />;

    case "videoScrollerDemo": {
      const videoCards = componentData.videoScrollerDemo?.videoCards ?? [];
      return (
        <LazyComponent
          name='videoScrollerDemo'
          cards={videoCards.map((card: any, index: number) => {
            const videoUrl = card.video ? getVideoUrl(card.video) : "";
            const coverImageProps = card.coverImage ? getImageProps(card.coverImage) : null;

            return {
              id: `video-card-${index}`,
              video: videoUrl || "",
              title: card.title,
              coverImage: coverImageProps,
            };
          })}
        />
      );
    }

    case "voiceCompareDemo": {
      const data = componentData.voiceCompareDemo;
      return <LazyComponent name='voiceCompareDemo' leftDisc={data?.leftDisc} rightDisc={data?.rightDisc} />;
    }

    case "simpleMusicDemo":
      return <LazyComponent name='simpleMusicDemo' />;

    case "videoComparisonDemo": {
      const data = componentData.videoComparisonDemo;
      return <LazyComponent name='videoComparisonDemo' videos={data?.videos} />;
    }

    case "audioTranscriptDemo": {
      const data = componentData.audioTranscriptDemo;
      const audioItems = (filterEntitiesArray(data?.items) || []) as AudioSample[];
      if (audioItems.length === 0) return null;
      return <LazyComponent name='audioTranscriptDemo' items={audioItems} playbackVariant={data?.playbackVariant} />;
    }

    default:
      return null;
  }
};
